import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import VendorOnboardingPage from "@/pages/VendorOnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import VendorsPage from "@/pages/VendorsPage";
import VendorDetailPage from "@/pages/VendorDetailPage";
import CustomersPage from "@/pages/CustomersPage";
import CustomerDetailPage from "@/pages/CustomerDetailPage";
import TransactionsPage from "@/pages/TransactionsPage";
import RewardsPage from "@/pages/RewardsPage";
import NudgeCenterPage from "@/pages/NudgeCenterPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import ApiDocsPage from "@/pages/ApiDocsPage";
import SettingsPage from "@/pages/SettingsPage";
import WalletPassDesignerPage from "@/pages/WalletPassDesignerPage";
import VendorCustomersPage from "@/pages/VendorCustomersPage";
import VendorDesignPage from "@/pages/VendorDesignPage";
import VendorNudgePage from "@/pages/VendorNudgePage";
import VendorSettingsPage from "@/pages/VendorSettingsPage";
import CustomerSignupPage from "@/pages/CustomerSignupPage";
import VendorEarningRulesPage from "@/pages/VendorEarningRulesPage";
import VendorRewardsPage from "@/pages/VendorRewardsPage";
import ScanPage from "@/pages/ScanPage";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading, needsOnboarding, isAdmin, isStaff } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (isStaff) return <Navigate to="/scan" replace />;
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  if (adminOnly && !isAdmin) return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function ScanRoute() {
  const { user, loading, isStaff, isVendor } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isStaff && !isVendor) return <Navigate to="/dashboard" replace />;
  return <ScanPage />;
}

function NudgeRouter() {
  const { isAdmin } = useAuth();
  return isAdmin ? <NudgeCenterPage /> : <VendorNudgePage />;
}

function AppRoutes() {
  const { user, loading, needsOnboarding, isStaff, defaultPath } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to={defaultPath} replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to={defaultPath} replace /> : <RegisterPage />} />
      <Route path="/onboarding" element={!user ? <Navigate to="/login" replace /> : (isStaff ? <Navigate to="/scan" replace /> : (!needsOnboarding ? <Navigate to="/dashboard" replace /> : <VendorOnboardingPage />))} />
      <Route path="/scan" element={<ScanRoute />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/vendors" element={<ProtectedRoute adminOnly><VendorsPage /></ProtectedRoute>} />
      <Route path="/vendors/:id" element={<ProtectedRoute><VendorDetailPage /></ProtectedRoute>} />
      <Route path="/vendors/:id/wallet-designer" element={<WalletPassDesignerPage />} />
      <Route path="/my-customers" element={<ProtectedRoute><VendorCustomersPage /></ProtectedRoute>} />
      <Route path="/design" element={<ProtectedRoute><VendorDesignPage /></ProtectedRoute>} />
      <Route path="/earning-rules" element={<ProtectedRoute><VendorEarningRulesPage /></ProtectedRoute>} />
      <Route path="/my-rewards" element={<ProtectedRoute><VendorRewardsPage /></ProtectedRoute>} />
      <Route path="/nudge" element={<ProtectedRoute><NudgeRouter /></ProtectedRoute>} />
      <Route path="/my-settings" element={<ProtectedRoute><VendorSettingsPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/customers/:id" element={<ProtectedRoute><CustomerDetailPage /></ProtectedRoute>} />
      <Route path="/transactions" element={<ProtectedRoute><TransactionsPage /></ProtectedRoute>} />
      <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
      <Route path="/approvals" element={<ProtectedRoute adminOnly><ApprovalsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute adminOnly><SettingsPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><ApiDocsPage /></ProtectedRoute>} />
      <Route path="/portal" element={<CustomerPortalPage />} />
      <Route path="/:slug/join" element={<CustomerSignupPage />} />
      <Route path="/:slug" element={<CustomerPortalPage />} />
      <Route path="/" element={<Navigate to={user ? defaultPath : "/login"} replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster richColors position="top-center" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
